






































import { Component, Vue, Prop, Provide, Watch } from "vue-property-decorator";
import { scrollElementIntoViewIfNeeded } from "@/helpers/DOMHelpers";

@Component
export default class AutoCompleteSearchInput extends Vue {
  @Prop({ default: () => [] }) private options!: {
    [searchKey: string]: any[] | null;
  };
  @Prop({ default: () => (item: string) => item }) private optionSelector!: (
    value: any
  ) => string;
  @Prop({ default: () => (item: string) => item }) private optionUuid!: (
    value: any
  ) => string;
  @Prop({ default: "" }) private value!: string;
  @Prop({ default: 3 }) private requestThreshold!: number;
  @Prop({ default: 100 }) private requestInterval!: number;
  @Prop({ default: true }) private submitNonMatching!: boolean;
  @Prop({ default: "Type your search term here..." })
  private placeholder!: string;

  @Provide() private inputText = this.value;
  @Provide() private searchText = this.inputText;
  @Provide() private selectedIndex = -1;
  @Provide() private isFocusTextInput = false;
  @Provide() private textLength = 0;
  @Provide() private canLoseFocus = true;
  @Provide() private showNoResults = false;

  @Watch("value")
  onValueChanged(newValue: string) {
    this.updateInputText(newValue);
  }

  @Watch("inputText")
  onInputTextChanged(newText: string) {
    if (!this.searchKey) {
      return;
    }
    if (this.searchKey && this.options[this.searchKey]) {
      return;
    }
    if (newText.length >= this.requestThreshold && this.selectedIndex < 0) {
      this.$emit("request-update", this.searchKey);
    }
  }

  @Watch("selectedIndex")
  onSelectedIndexChanged(newIndex: number) {
    const matches = this.$refs[`option-${newIndex}`];
    if (!Array.isArray(matches) || !matches.length) {
      return;
    }
    const [ref] = matches;
    if (!(ref instanceof HTMLElement)) {
      return;
    }
    scrollElementIntoViewIfNeeded(ref);
  }

  get searchKey(): string | null {
    if (this.inputText.length < this.requestThreshold) {
      return null;
    }
    return this.inputText.substr(0, 3).toLowerCase();
  }

  get showOptionsList() {
    if (this.showNoResults || !this.isFocusTextInput || !this.searchKey) {
      return false;
    }
    return this.searchableOptions.length > 0;
  }

  get searchableOptions() {
    const existingKeys: { [key: string]: boolean } = {};
    return Object.keys(this.options)
      .reduce((acc: any[], key) => {
        return [...acc, ...(this.options[key] || [])];
      }, [])
      .filter(option => {
        if (existingKeys[this.optionKey(option)]) {
          return;
        }
        const isMatchingInputText = this.optionLabel(option)
          .toLowerCase()
          .includes(this.searchText.toLowerCase());
        if (isMatchingInputText) {
          existingKeys[this.optionKey(option)] = true;
        }
        return isMatchingInputText;
      });
  }

  get showSearchingIndicator() {
    if (!this.searchKey) {
      return false;
    }
    return this.options[this.searchKey] === null;
  }

  private optionLabel(option?: any[]): string {
    if (!option) {
      return "";
    }
    return this.optionSelector(option) || "";
  }

  private optionKey(option?: any[]): string {
    if (!option) {
      return "";
    }
    return this.optionUuid(option) || "";
  }

  private updateInputText(text: string) {
    this.inputText = text;
    this.textLength = text.length;
  }

  handleInputChange(event: InputEvent) {
    if (!event || !(event.target instanceof HTMLInputElement)) {
      return;
    }
    this.showNoResults = false;
    this.selectedIndex = -1;
    this.updateInputText(event.target.value);
    this.$emit("change", event.target.value);
  }

  handleFocusChange(isFocus: boolean) {
    if (!isFocus && !this.canLoseFocus) {
      return;
    }
    this.isFocusTextInput = isFocus;
    this.selectedIndex = -1;
  }

  handleOptionHover(index: number) {
    this.selectedIndex = index;
  }

  handleOptionMouseDown() {
    this.canLoseFocus = false;
    setTimeout(() => {
      this.canLoseFocus = true;
    }, 10);
  }

  handleOptionSelected(index: number, defaultAction = "change") {
    if (index >= 0) {
      this.inputText = this.optionLabel(this.searchableOptions[index]);
      this.$emit(
        defaultAction,
        this.optionLabel(this.searchableOptions[index]),
        this.searchableOptions[index]
      );
    } else {
      this.searchText = this.inputText;
      const [firstMatchingKeyword] = this.searchableOptions;
      this.submitWildSearch(this.searchText, firstMatchingKeyword);
    }
  }

  handleKeyDown(event: KeyboardEvent) {
    const { keyCode } = event;
    if (keyCode === 40) {
      const newIndex = this.selectedIndex + 1;
      this.selectedIndex =
        newIndex >= this.searchableOptions.length ? 0 : newIndex;
      this.handleOptionSelected(this.selectedIndex);
      event.preventDefault();
    } else if (keyCode === 38) {
      const newIndex = this.selectedIndex - 1;
      this.selectedIndex =
        newIndex < 0 ? this.searchableOptions.length - 1 : newIndex;
      this.handleOptionSelected(this.selectedIndex);
      event.preventDefault();
    } else if (keyCode === 27) {
      this.selectedIndex = -1;
      this.textLength = 0;
      event.preventDefault();
    } else if (keyCode === 13) {
      this.selectedIndex = -1;
      this.textLength = 0;
      this.handleOptionSelected(this.selectedIndex);
      event.preventDefault();
    } else {
      this.searchText = this.inputText;
    }
  }

  submitWildSearch(search: string, match: any) {
    if (
      !this.submitNonMatching &&
      (!match || search.toLowerCase() != this.optionLabel(match).toLowerCase())
    ) {
      this.showNoResults = true;
      return;
    }
    this.$emit("submit", this.inputText, match);
  }
}
